import { AnalysisCidList } from '@/features/analyses/pme/components/analysis-cid-list.component';
import { useCidDescriptions } from '@/features/analyses/pme/use-cid-descriptions.hook';
import { AnalysisTabSkeleton } from '../../components/analysis-tabs-card.component';
import { Tag, Text } from '@noorden-insurtech/alba-design-system';
import {
  PME_ANALYSIS_AGE_CAPITAL,
  PME_ANALYSIS_AVERAGE_MONTHLY_RATE,
  PME_ANALYSIS_COVERAGES,
  PME_ANALYSIS_CRITERIA,
  PME_ANALYSIS_REMOVED_LIVES,
  PME_ANALYSIS_SERVICES,
  type PmeAcceptanceCriterion,
  type PmeAgeCapitalRow,
  type PmeCoverageLine,
  type PmeCriterionStatus,
  type PmeRemovedLifeRow,
  type PmeServiceLine,
} from '../pme-analysis.mock';

const DASH = '—';

function orDash(value?: string): string {
  return value != null && value.trim() !== '' ? value : DASH;
}

function EmptyRow() {
  return (
    <div className='py-3'>
      <Text className='font-montserrat text-sm text-alba-gray-600'>{DASH}</Text>
    </div>
  );
}

// ─── Aba "Critérios de Aceitação" ───────────────────────────────────────────

/**
 * Status de uma sub-regra. Cada linha responde por si: a que reprovou fica
 * "Análise", a que passou fica "OK" — independente das outras. Linha sem
 * status próprio (fallback, mensagem fora do catálogo) herda o do critério.
 */
function lineStatusAt(
  criterion: PmeAcceptanceCriterion,
  index: number
): PmeCriterionStatus {
  return criterion.lineStatus?.[index] ?? criterion.status;
}

/** Laranja quando a sub-regra é motivo de análise; cinza quando está OK. */
function lineColor(criterion: PmeAcceptanceCriterion, index: number): string {
  return lineStatusAt(criterion, index) === 'Análise'
    ? 'text-[#E8833A]'
    : 'text-[#777B7C]';
}

function CriterionStatusTag({ status }: { status: PmeCriterionStatus }) {
  if (status === 'OK') {
    return (
      <Tag.Root colorScheme='success' size='sm'>
        <Tag.Label>OK</Tag.Label>
      </Tag.Root>
    );
  }
  return (
    <Tag.Root colorScheme='yellow' size='sm'>
      <Tag.Label>Análise</Tag.Label>
    </Tag.Root>
  );
}

export function PmeAcceptanceCriteriaTab({
  criteria = PME_ANALYSIS_CRITERIA,
}: {
  criteria?: PmeAcceptanceCriterion[];
} = {}) {
  return (
    <div className='flex flex-col overflow-x-auto'>
      <div className='min-w-[720px]'>
        {/* Cabeçalho */}
        <div className='grid grid-cols-[1.3fr_3fr_0.9fr_0.6fr] gap-4 pb-4'>
          <Text className='font-montserrat text-xs font-bold text-alba-black-500'>
            Critérios
          </Text>
          <Text className='font-montserrat text-xs font-bold text-alba-black-500'>
            Regras
          </Text>
          <Text className='text-right font-montserrat text-xs font-bold text-alba-black-500'>
            Resultado
          </Text>
          <Text className='text-center font-montserrat text-xs font-bold text-alba-black-500'>
            Status
          </Text>
        </div>

        {/* Linhas */}
        {criteria.length === 0 ? (
          <EmptyRow />
        ) : (
          criteria.map(criterion => {
            const isUnderAnalysis = criterion.status === 'Análise';
            const labelColor = isUnderAnalysis
              ? 'text-[#E8833A]'
              : 'text-[#777B7C]';
            const textColor = isUnderAnalysis
              ? 'text-[#E8833A]'
              : 'text-[#777B7C]';

            return (
              <div
                key={criterion.name}
                className='grid grid-cols-[1.3fr_3fr_0.9fr_0.6fr] items-center gap-4 py-3'
              >
                <Text
                  className={`font-montserrat text-sm font-medium ${labelColor}`}
                >
                  {orDash(criterion.name)}
                </Text>

                <div className='flex flex-col gap-1'>
                  {criterion.rules.length === 0 ? (
                    <Text className={`font-montserrat text-xs ${textColor}`}>
                      {DASH}
                    </Text>
                  ) : (
                    criterion.rules.map((rule, index) => (
                      <Text
                        key={index}
                        className={`font-montserrat text-xs whitespace-pre-line ${lineColor(criterion, index)}`}
                      >
                        {orDash(rule)}
                      </Text>
                    ))
                  )}
                </div>

                <div className='flex flex-col gap-1 text-right'>
                  {criterion.results.length === 0 ? (
                    <Text className={`font-montserrat text-xs ${textColor}`}>
                      {DASH}
                    </Text>
                  ) : (
                    criterion.results.map((result, index) => (
                      <Text
                        key={index}
                        className={`font-montserrat text-xs whitespace-pre-line ${lineColor(criterion, index)}`}
                      >
                        {orDash(result)}
                      </Text>
                    ))
                  )}
                </div>

                <div className='flex flex-col items-center gap-1'>
                  {criterion.rules.length === 0 ? (
                    <CriterionStatusTag status={criterion.status} />
                  ) : (
                    criterion.rules.map((_, index) => (
                      <CriterionStatusTag
                        key={index}
                        status={lineStatusAt(criterion, index)}
                      />
                    ))
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ─── Aba "Coberturas e Serviços" ────────────────────────────────────────────

export function PmeCoveragesServicesTab({
  coverages = PME_ANALYSIS_COVERAGES,
  services = PME_ANALYSIS_SERVICES,
  averageMonthlyRate = PME_ANALYSIS_AVERAGE_MONTHLY_RATE,
}: {
  coverages?: PmeCoverageLine[];
  services?: PmeServiceLine[];
  averageMonthlyRate?: string;
} = {}) {
  return (
    <div className='flex flex-col gap-8 overflow-x-auto'>
      {/* Coberturas */}
      <div className='min-w-[640px]'>
        <div className='grid grid-cols-[2fr_1.2fr_1fr_1fr] gap-4 pb-4'>
          <Text className='font-montserrat text-xs font-bold text-alba-black-500'>
            Coberturas
          </Text>
          <Text className='text-right font-montserrat text-xs font-bold text-alba-black-500'>
            Capital Segurado (I$) Total
          </Text>
          <Text className='text-right font-montserrat text-xs font-bold text-alba-black-500'>
            Taxa Média (‰)
          </Text>
          <Text className='text-right font-montserrat text-xs font-bold text-alba-black-500'>
            Prêmio Mensal
          </Text>
        </div>

        {coverages.length === 0 ? (
          <EmptyRow />
        ) : (
          coverages.map(coverage => (
            <div
              key={coverage.name}
              className='grid grid-cols-[2fr_1.2fr_1fr_1fr] gap-4 py-3'
            >
              <div className='flex flex-col'>
                <Text className='font-montserrat text-sm text-alba-black-500'>
                  {orDash(coverage.name)}
                </Text>
                {coverage.detail ? (
                  <Text className='font-montserrat text-xs text-alba-gray-600'>
                    {coverage.detail}
                  </Text>
                ) : null}
              </div>
              <Text className='text-right font-montserrat text-sm text-alba-gray-600'>
                {orDash(coverage.totalInsuredCapital)}
              </Text>
              <Text className='text-right font-montserrat text-sm text-alba-gray-600'>
                {orDash(coverage.averageRate)}
              </Text>
              <Text className='text-right font-montserrat text-sm text-alba-gray-600'>
                {orDash(coverage.monthlyPremium)}
              </Text>
            </div>
          ))
        )}

        <div className='grid grid-cols-[2fr_1.2fr_1fr_1fr] gap-4 py-3'>
          <Text className='font-montserrat text-sm font-bold text-alba-black-500'>
            Taxa Média Mensal
          </Text>
          <span />
          <Text className='text-right font-montserrat text-sm font-bold text-alba-black-500'>
            {orDash(averageMonthlyRate)}
          </Text>
          <span />
        </div>
      </div>

      {/* Serviços e Assistências */}
      <div className='min-w-[640px]'>
        <div className='grid grid-cols-[3.2fr_1fr_1fr] gap-4 pb-4'>
          <Text className='font-montserrat text-xs font-bold text-alba-black-500'>
            Serviços e Assistências
          </Text>
          <Text className='text-right font-montserrat text-xs font-bold text-alba-black-500'>
            Valor por Segurado
          </Text>
          <Text className='text-right font-montserrat text-xs font-bold text-alba-black-500'>
            Valor Mensal
          </Text>
        </div>

        {services.length === 0 ? (
          <EmptyRow />
        ) : (
          services.map(service => (
            <div
              key={service.name}
              className='grid grid-cols-[3.2fr_1fr_1fr] gap-4 py-3'
            >
              <Text className='font-montserrat text-sm text-alba-black-500'>
                {orDash(service.name)}
              </Text>
              <Text className='text-right font-montserrat text-sm text-alba-gray-600'>
                {orDash(service.valuePerInsured)}
              </Text>
              <Text className='text-right font-montserrat text-sm text-alba-gray-600'>
                {orDash(service.monthlyValue)}
              </Text>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ─── Aba "Idade / Capital" ──────────────────────────────────────────────────

export function PmeAgeCapitalTab({
  rows = PME_ANALYSIS_AGE_CAPITAL,
  isLoading = false,
}: {
  rows?: PmeAgeCapitalRow[];
  isLoading?: boolean;
} = {}) {
  return (
    <div className='overflow-x-auto'>
      <div className='min-w-[480px]'>
        <div className='grid grid-cols-[1fr_1.2fr_0.8fr] gap-4 pb-4'>
          <Text className='font-montserrat text-xs font-bold text-alba-black-500'>
            Idade
          </Text>
          <Text className='text-right font-montserrat text-xs font-bold text-alba-black-500'>
            Capital
          </Text>
          <Text className='text-right font-montserrat text-xs font-bold text-alba-black-500'>
            Vidas
          </Text>
        </div>

        {isLoading ? (
          <AnalysisTabSkeleton
            gridClassName='grid-cols-[1fr_1.2fr_0.8fr]'
            columns={3}
          />
        ) : rows.length === 0 ? (
          <EmptyRow />
        ) : (
          rows.map((row, index) => (
            <div
              key={index}
              className='grid grid-cols-[1fr_1.2fr_0.8fr] gap-4 py-2.5'
            >
              <Text className='font-montserrat text-sm text-alba-black-500'>
                {orDash(row.age)}
              </Text>
              <Text className='text-right font-montserrat text-sm text-alba-gray-600'>
                {orDash(row.capital)}
              </Text>
              <Text className='text-right font-montserrat text-sm text-alba-gray-600'>
                {orDash(row.lives)}
              </Text>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ─── Aba "Afastados" ────────────────────────────────────────────────────────

export function PmeAfastadosTab({
  rows = PME_ANALYSIS_REMOVED_LIVES,
  isLoading = false,
}: {
  rows?: PmeRemovedLifeRow[];
  isLoading?: boolean;
} = {}) {
  const { data: cidDescriptions } = useCidDescriptions(
    rows.flatMap(row => row.cids)
  );
  return (
    <div className='overflow-x-auto'>
      <div className='min-w-[640px]'>
        <div className='grid grid-cols-[2fr_1fr_0.7fr_1.2fr_1.6fr] gap-4 pb-4'>
          <Text className='font-montserrat text-xs font-bold text-alba-black-500'>
            Nome
          </Text>
          <Text className='font-montserrat text-xs font-bold text-alba-black-500'>
            Sexo
          </Text>
          <Text className='font-montserrat text-xs font-bold text-alba-black-500'>
            Idade
          </Text>
          <Text className='text-right font-montserrat text-xs font-bold text-alba-black-500'>
            Capital
          </Text>
          <Text className='text-left font-montserrat text-xs font-bold text-alba-black-500'>
            CID
          </Text>
        </div>

        {isLoading ? (
          <AnalysisTabSkeleton
            gridClassName='grid-cols-[2fr_1fr_0.7fr_1.2fr_1.6fr]'
            columns={5}
          />
        ) : rows.length === 0 ? (
          <EmptyRow />
        ) : (
          rows.map((row, index) => (
            <div
              key={index}
              className='grid grid-cols-[2fr_1fr_0.7fr_1.2fr_1.6fr] gap-4 py-3'
            >
              <Text className='font-montserrat text-sm text-alba-black-500'>
                {orDash(row.name)}
              </Text>
              <Text className='font-montserrat text-sm text-alba-gray-600'>
                {orDash(row.sex)}
              </Text>
              <Text className='font-montserrat text-sm text-alba-gray-600'>
                {orDash(row.age)}
              </Text>
              <Text className='text-right font-montserrat text-sm text-alba-gray-600'>
                {orDash(row.capital)}
              </Text>
              <Text className='text-right font-montserrat text-sm text-alba-gray-600'>
                <AnalysisCidList codes={row.cids} descriptions={cidDescriptions} />
              </Text>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
